import React, { useState, useCallback, useEffect } from 'react';
import ReactFlow, { 
  MiniMap, 
  Controls, 
  Background, 
  ReactFlowProvider,
  applyNodeChanges
} from 'reactflow';
import 'reactflow/dist/style.css';
import { themeStyles, customStyles } from './ArtemisStyles';
import { nodeTypes } from './ArtemisCustomNodes';

// Repository evolution nodes
const repositoryNodes = [
  // Title
  {
    id: 'title',
    data: { label: '🗂️ Repository-Wide Optimization' },
    position: { x: 500, y: 20 },
    style: { ...themeStyles.problemNode, width: 400 },
  },
  
  // Repository Structure
  {
    id: 'repo-label',
    data: { label: '📁 Your Repository Structure' },
    position: { x: 50, y: 120 },
    style: { ...themeStyles.metricNode, width: 200 },
  },
  
  // Files with issues
  {
    id: 'api-file',
    type: 'repoFile',
    data: { 
      filename: 'api/endpoints.py',
      icon: '🌐',
      optimization: 'Slow database queries',
      impact: 'High'
    },
    position: { x: 50, y: 200 },
  },
  {
    id: 'auth-file',
    type: 'repoFile',
    data: { 
      filename: 'auth/jwt_handler.py',
      icon: '🔐',
      optimization: 'Token generation',
      impact: 'Medium'
    },
    position: { x: 50, y: 290 },
  },
  {
    id: 'data-file',
    type: 'repoFile',
    data: { 
      filename: 'data/processor.py',
      icon: '📊',
      optimization: 'Memory inefficient',
      impact: 'High'
    },
    position: { x: 50, y: 380 },
  },
  {
    id: 'utils-file',
    type: 'repoFile',
    data: { 
      filename: 'utils/helpers.py',
      icon: '🔧',
      optimization: 'Redundant loops',
      impact: 'Low'
    },
    position: { x: 50, y: 470 },
  },
  {
    id: 'cache-file',
    type: 'repoFile',
    data: { 
      filename: 'cache/redis_client.py',
      icon: '💾',
      optimization: 'Connection pooling',
      impact: 'Medium'
    },
    position: { x: 50, y: 560 },
  },
  
  // Suggestion Space
  {
    id: 'suggestion-label',
    data: { label: '💡 Suggestion Generation\n(Multiple per file)' },
    position: { x: 300, y: 120 },
    style: { ...themeStyles.analysisNode, width: 220 },
  },
  
  // API suggestions
  {
    id: 'api-s1',
    data: { label: 'Batch queries' },
    position: { x: 280, y: 200 },
    style: { ...themeStyles.metricNode, width: 100, fontSize: '12px' },
  },
  {
    id: 'api-s2',
    data: { label: 'Add caching' },
    position: { x: 390, y: 200 },
    style: { ...themeStyles.metricNode, width: 100, fontSize: '12px' },
  },
  {
    id: 'api-s3',
    data: { label: 'Query optimization' },
    position: { x: 500, y: 200 },
    style: { ...themeStyles.metricNode, width: 110, fontSize: '12px' },
  },
  
  // Auth suggestions
  {
    id: 'auth-s1',
    data: { label: 'Async generation' },
    position: { x: 320, y: 290 },
    style: { ...themeStyles.metricNode, width: 110, fontSize: '12px' },
  },
  {
    id: 'auth-s2',
    data: { label: 'Cache tokens' },
    position: { x: 450, y: 290 },
    style: { ...themeStyles.metricNode, width: 100, fontSize: '12px' },
  },
  
  // Data processor suggestions
  {
    id: 'data-s1',
    data: { label: 'Stream processing' },
    position: { x: 320, y: 380 },
    style: { ...themeStyles.metricNode, width: 110, fontSize: '12px' },
  },
  {
    id: 'data-s2',
    data: { label: 'Use generators' },
    position: { x: 450, y: 380 },
    style: { ...themeStyles.metricNode, width: 100, fontSize: '12px' },
  },
  
  // Evolution Engine
  {
    id: 'evolution-engine',
    data: { label: '🧬 Evolutionary Search Engine\nFinding Optimal Combinations' },
    position: { x: 700, y: 200 },
    style: { ...themeStyles.uniqueFeatureNode, width: 280 },
  },
  
  // Combination Branches
  {
    id: 'branch-a',
    data: { label: '🔀 Branch A\napi: Batch queries\nauth: Async\ndata: Streaming\nPerformance Focus' },
    position: { x: 700, y: 320 },
    style: { ...themeStyles.suggestionNode, width: 250, fontSize: '12px' },
  },
  {
    id: 'branch-b',
    data: { label: '🔀 Branch B\napi: Add caching\nauth: Cache tokens\ndata: Generators\nBalance Focus' },
    position: { x: 700, y: 420 },
    style: { ...themeStyles.suggestionNode, width: 250, fontSize: '12px' },
  },
  {
    id: 'branch-c',
    data: { label: '🔀 Branch C\napi: Query optimization\nauth: Async\ndata: Streaming\nMemory Focus' },
    position: { x: 700, y: 520 },
    style: { ...themeStyles.suggestionNode, width: 250, fontSize: '12px' },
  },
  
  // Impact Analysis
  {
    id: 'impact-label',
    data: { label: '📊 Holistic Impact Analysis' },
    position: { x: 1050, y: 120 },
    style: { ...themeStyles.analysisNode, width: 200 },
  },
  
  // Branch impacts with detailed metrics
  {
    id: 'impact-b',
    data: { label: '🏆 Branch B Winner\nPerformance: +52%\nMemory: -35%\nReadability: Good\nRisk: Low\nOverall: 4.8/5' },
    position: { x: 1050, y: 200 },
    style: { ...themeStyles.scoreNode, width: 180, height: 120, borderRadius: '15px', fontSize: '13px' },
  },
  {
    id: 'impact-a',
    data: { label: 'Branch A\nPerformance: +48%\nMemory: -25%\nReadability: Fair\nRisk: Medium\nOverall: 4.2/5' },
    position: { x: 1050, y: 340 },
    style: { ...themeStyles.metricNode, width: 180, fontSize: '12px' },
  },
  {
    id: 'impact-c',
    data: { label: 'Branch C\nPerformance: +35%\nMemory: -40%\nReadability: Good\nRisk: Low\nOverall: 4.0/5' },
    position: { x: 1050, y: 460 },
    style: { ...themeStyles.metricNode, width: 180, fontSize: '12px' },
  },
  
  // Dependencies Check
  {
    id: 'dependencies',
    data: { label: '🔗 Dependency Analysis\n• Cross-file impacts\n• Breaking changes\n• Test coverage' },
    position: { x: 1050, y: 580 },
    style: { ...themeStyles.analysisNode, width: 180, fontSize: '13px' },
  },
  
  // Final Output
  {
    id: 'final-pr',
    data: { label: '🔀 Automated Pull Request\n\n✅ 5 files changed\n➕ 127 additions\n➖ 89 deletions\n📈 +52% performance' },
    position: { x: 600, y: 650 },
    style: { ...themeStyles.uniqueFeatureNode, width: 250 },
  }
];

const repositoryEdges = [
  // Files to suggestions
  { id: 'r1', source: 'api-file', target: 'api-s1', style: themeStyles.edgeStyle },
  { id: 'r2', source: 'api-file', target: 'api-s2', style: themeStyles.edgeStyle },
  { id: 'r3', source: 'api-file', target: 'api-s3', style: themeStyles.edgeStyle },
  { id: 'r4', source: 'auth-file', target: 'auth-s1', style: themeStyles.edgeStyle },
  { id: 'r5', source: 'auth-file', target: 'auth-s2', style: themeStyles.edgeStyle },
  { id: 'r6', source: 'data-file', target: 'data-s1', style: themeStyles.edgeStyle },
  { id: 'r7', source: 'data-file', target: 'data-s2', style: themeStyles.edgeStyle },
  
  // Suggestions to evolution
  { id: 'r8', source: 'api-s1', target: 'evolution-engine', style: themeStyles.evolutionEdgeStyle },
  { id: 'r9', source: 'api-s2', target: 'evolution-engine', style: themeStyles.evolutionEdgeStyle },
  { id: 'r10', source: 'auth-s1', target: 'evolution-engine', style: themeStyles.evolutionEdgeStyle },
  { id: 'r11', source: 'data-s1', target: 'evolution-engine', style: themeStyles.evolutionEdgeStyle },
  
  // Evolution to branches
  { id: 'r12', source: 'evolution-engine', target: 'branch-a', animated: true, style: themeStyles.evolutionEdgeStyle },
  { id: 'r13', source: 'evolution-engine', target: 'branch-b', animated: true, style: themeStyles.evolutionEdgeStyle },
  { id: 'r14', source: 'evolution-engine', target: 'branch-c', animated: true, style: themeStyles.evolutionEdgeStyle },
  
  // Branches to impact
  { id: 'r15', source: 'branch-a', target: 'impact-a', style: themeStyles.feedbackEdgeStyle },
  { id: 'r16', source: 'branch-b', target: 'impact-b', animated: true, style: themeStyles.feedbackEdgeStyle },
  { id: 'r17', source: 'branch-c', target: 'impact-c', style: themeStyles.feedbackEdgeStyle },
  
  // Dependencies check
  { id: 'r18', source: 'impact-b', target: 'dependencies', style: themeStyles.edgeStyle },
  
  // To final PR
  { id: 'r19', source: 'dependencies', target: 'final-pr', animated: true, style: themeStyles.evolutionEdgeStyle },
];

const ArtemisRepositoryEvolution = () => {
  const [nodes, setNodes] = useState(repositoryNodes);
  const [edges] = useState(repositoryEdges);

  // Add custom styles on mount
  useEffect(() => {
    const styleSheet = document.createElement("style");
    styleSheet.innerText = customStyles;
    document.head.appendChild(styleSheet);
    
    return () => {
      document.head.removeChild(styleSheet);
    };
  }, []);

  const onNodesChange = useCallback((changes) => {
    setNodes((nds) => applyNodeChanges(changes, nds));
  }, []);

  return (
    <ReactFlowProvider>
      <div style={{ 
        height: '100vh', 
        width: '100%', 
        background: 'linear-gradient(135deg, #2c1810 0%, #8e4a2c 50%, #c76b3f 100%)',
        position: 'relative'
      }}>
        <h2 style={{ 
          textAlign: 'center', 
          color: '#ffffff', 
          padding: '20px', 
          margin: '0',
          fontFamily: 'Arial, sans-serif',
          fontSize: '28px',
          textShadow: '0 2px 10px rgba(0,0,0,0.5)'
        }}>
          🗂️ Repository-Wide Evolution
        </h2>
        
        <div style={{
          position: 'absolute',
          top: '70px',
          left: '50%',
          transform: 'translateX(-50%)',
          padding: '8px 20px',
          background: 'rgba(255,255,255,0.1)',
          borderRadius: '20px',
          color: '#ffffff',
          fontSize: '14px',
          zIndex: 100
        }}>
          Optimizing entire repositories by finding the best combination of changes across multiple files
        </div>
        
        <div style={{ 
          position: 'absolute',
          top: '120px',
          left: 0,
          right: 0,
          bottom: 0,
          width: '100%',
          height: 'calc(100% - 120px)'
        }}>
          <ReactFlow
            nodes={nodes}
            edges={edges}
            onNodesChange={onNodesChange}
            nodeTypes={nodeTypes}
            fitView
            fitViewOptions={{
              padding: 0.1,
              minZoom: 0.5,
              maxZoom: 1.0
            }}
            minZoom={0.4}
            maxZoom={1.5}
            defaultViewport={{ x: 0, y: 0, zoom: 0.7 }}
            nodesDraggable={true}
            nodesConnectable={false}
            elementsSelectable={true}
            panOnDrag={true}
            panOnScroll={true}
            zoomOnScroll={true}
            zoomOnPinch={true}
          >
            <MiniMap 
              style={{
                height: 120,
                width: 180,
                backgroundColor: 'rgba(0,0,0,0.3)',
              }}
              nodeColor={(node) => {
                return node.style?.background || '#666';
              }}
              nodeStrokeColor={() => '#ffffff'}
              nodeBorderRadius={2}
            />
            <Controls />
            <Background 
              variant="dots" 
              color="rgba(255,255,255,0.05)" 
              gap={20} 
              size={1} 
            />
          </ReactFlow>
        </div>

        {/* Key Insights */}
        <div style={{
          position: 'absolute',
          bottom: '20px',
          left: '20px',
          background: 'rgba(0,0,0,0.6)',
          padding: '15px',
          borderRadius: '15px',
          backdropFilter: 'blur(10px)',
          color: 'white',
          fontSize: '13px',
          maxWidth: '300px'
        }}>
          <div style={{ marginBottom: '10px', fontWeight: 'bold' }}>🎯 Repository Impact</div>
          <div style={{ marginBottom: '5px' }}>Files Analyzed: <span style={{ color: '#4facfe' }}>5</span></div>
          <div style={{ marginBottom: '5px' }}>Suggestions Generated: <span style={{ color: '#f9d423' }}>12</span></div>
          <div style={{ marginBottom: '5px' }}>Combinations Tested: <span style={{ color: '#ff9800' }}>48</span></div>
          <div>Best Impact: <span style={{ color: '#4caf50', fontWeight: 'bold' }}>+52% Performance</span></div>
        </div>
      </div>
    </ReactFlowProvider>
  );
};

export default ArtemisRepositoryEvolution; 